import { mountVeivoValidationFlow, VeivoValidationFlowHandle } from "@veivo/web-sdk";
import { useEffect, useRef, useState } from "react";
import { Button, Modal, Typography } from "@/components/atoms";
import { veivoHandoffUrl } from "@/helpers/veivoHandoffUrl";
import { veivoThemeFromBranding } from "@/helpers/veivoTheme";
import { useOperator } from "@/hooks";
import { vehicleApi } from "@/redux/apis/vehicle/vehicleApi";
import { useGetVeivoValidationStatusQuery, useStartVeivoRenewalMutation, veivoApi } from "@/redux/apis/vehicle/veivoApi";
import { useAppDispatch } from "@/redux/hooks";
import { addToast } from "@/utils";

interface RenewDocumentsModalProps {
  vehicleId: string;
  /** Veivo doc types to renew; null = closed. */
  docTypes: string[] | null;
  onClose: () => void;
}

/**
 * Renew some of a verified vehicle's documents through Veivo (DEV-1732):
 * the SDK in renewal mode — documents only, no VIN step, no walk-around.
 *
 * Completion is server-authoritative, as in the add-vehicle wizard: the modal
 * closes once the link row (fed by Veivo's webhook, which also projects the new
 * documents onto the vehicle) reports the renewal complete — never on the SDK's
 * client-side callback alone.
 */
export const RenewDocumentsModal = ({ vehicleId, docTypes, onClose }: RenewDocumentsModalProps) => {
  const dispatch = useAppDispatch();
  const { branding } = useOperator();
  const hostRef = useRef<HTMLDivElement>(null);
  const flowRef = useRef<VeivoValidationFlowHandle | null>(null);
  const finishedRef = useRef(false);
  const [validationId, setValidationId] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [startRenewal] = useStartVeivoRenewalMutation();
  const open = docTypes !== null;
  const docTypesKey = docTypes?.join(",") ?? "";

  const { data: status } = useGetVeivoValidationStatusQuery(validationId ?? "", {
    skip: !validationId,
    pollingInterval: 4000,
  });

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    finishedRef.current = false;
    setStartError(null);
    setValidationId(null);

    startRenewal({ vehicleId, docTypes: docTypesKey.split(",") })
      .unwrap()
      .then((session) => {
        const host = hostRef.current;
        if (cancelled || !host) return;
        setValidationId(session.validationId);
        flowRef.current = mountVeivoValidationFlow(host, {
          service: { url: session.veivoBaseUrl },
          theme: veivoThemeFromBranding(branding),
          resumeValidationId: session.validationId,
          sessionToken: session.sessionToken,
          handoffUrl: (params) => veivoHandoffUrl(params, branding?.primary),
          // The service must ALSO have VEIVO_ALLOW_TEST_SKIP on, so this flag
          // alone cannot bypass anything — and it is unset in production builds.
          allowTestSkip: import.meta.env.VITE_VEIVO_ALLOW_TEST_SKIP === "true",
          onError: (err) => console.error("Veivo renewal error:", err),
        });
      })
      .catch((err) => {
        console.error("Failed to start the Veivo renewal:", err);
        if (!cancelled) setStartError(err?.data?.message ?? "The renewal could not be started. Please try again.");
      });

    return () => {
      cancelled = true;
      void flowRef.current?.dispose();
      flowRef.current = null;
    };
  }, [open, vehicleId, docTypesKey, startRenewal, branding]);

  useEffect(() => {
    if (status?.status !== "complete" || finishedRef.current) return;
    finishedRef.current = true;
    // The webhook that marked it complete has projected the new documents;
    // refetch the vehicle (tick, badges) and its Veivo documents.
    dispatch(vehicleApi.util.invalidateTags(["Vehicle"]));
    dispatch(veivoApi.util.invalidateTags(["VeivoValidation"]));
    addToast("success", "Documents renewed");
    onClose();
  }, [status?.status, dispatch, onClose]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Renew documents"
      description="Photograph the new documents. They are checked by vehicle verification before they replace the old ones."
      variant="web-large"
      renderButtons={() => (
        <Button variant="tertiary" size="md" onClick={onClose}>
          Close
        </Button>
      )}
    >
      {startError ? <Typography className="text-danger">{startError}</Typography> : <div ref={hostRef} className="min-h-[320px]" />}
    </Modal>
  );
};
